import { apiFetch } from './api'

const BASE = import.meta.env.VITE_API_URL || 'http://92.53.107.15'
const ANALYZE_URL  = `${BASE}/api/analyze`
const START_URL    = `/api/analyze/start`
const JOB_URL      = (id) => `${BASE}/api/analyze/job/${id}`

const POLL_INTERVAL = 4000   // ms between status checks
const POLL_TIMEOUT  = 660000 // 11 min total

/**
 * Async polling wrapper for analyzeAll.
 * 1. POST /api/analyze/start  → job_id  (responds in <1s, bypasses CF timeout)
 * 2. GET  /api/analyze/job/id → polls every 4s until done/error
 */
export async function analyzeAll(context, extraData = {}) {
  console.log('[ANALYZE] starting async job...')

  // Start the job (apiFetch adds Authorization header)
  const startRes = await apiFetch(START_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ goal: 'all', context, ...extraData }),
  })
  if (!startRes.ok) {
    const txt = await startRes.text()
    throw new Error(`API ${startRes.status}: ${txt}`)
  }
  const { job_id } = await startRes.json()
  if (!job_id) throw new Error('No job_id returned from server')
  console.log('[ANALYZE] job started:', job_id)

  // Poll for result
  const deadline = Date.now() + POLL_TIMEOUT
  let networkErrors = 0
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL))

    let job
    try {
      const pollRes = await fetch(JOB_URL(job_id))
      if (!pollRes.ok) { networkErrors = 0; continue }
      job = await pollRes.json()
      networkErrors = 0
    } catch {
      // Transient network error — retry up to 5 times before giving up
      networkErrors++
      console.warn(`[ANALYZE] poll network error #${networkErrors}`)
      if (networkErrors >= 5) throw new Error('Соединение с сервером прервано. Попробуйте ещё раз.')
      continue
    }

    console.log('[ANALYZE] poll status:', job.status)

    if (job.status === 'done') {
      const result = job.result
      if (result?.error) throw new Error(result.error)
      const u = result?.usage || {}
      console.log(`[ANALYZE] done — in:${u.input_tokens} out:${u.output_tokens} cost:$${u.cost_usd}`)
      return result
    }

    if (job.status === 'error') {
      throw new Error(job.error || 'Ошибка анализа на сервере')
    }

    if (job.status === 'not_found') {
      // Server may have restarted mid-job — wait a bit, then give up
      networkErrors++
      if (networkErrors >= 3) throw new Error('Сервер перезагрузился во время анализа. Попробуйте ещё раз.')
      continue
    }
  }

  throw new Error('Анализ занял слишком много времени (>11 мин). Попробуйте ещё раз.')
}

// Backward-compat wrappers (legacy single-goal analyze calls)
async function callClaude(goal, context) {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 90000)
  try {
    const res = await fetch(ANALYZE_URL, {
      signal: controller.signal,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ goal, context }),
    })
    if (!res.ok) throw new Error(`API ${res.status}`)
    return res.json()
  } finally {
    clearTimeout(timeoutId)
  }
}

export async function analyzePersonality(context) {
  return callClaude('personality', context)
}

export async function analyzeCompetencies(context) {
  return callClaude('competencies', context)
}

export async function analyzeCareer(personality, competencies) {
  return callClaude('career', JSON.stringify({ personality, competencies }))
}
